import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import { Calendar, CheckCircle, Send, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import PageLayout from '../components/PageLayout';

const TripInterestPage = () => {
  const { user } = useAuth();
  const [trips, setTrips] = useState([]);
  const [tripCounts, setTripCounts] = useState({});
  const [selectedTrips, setSelectedTrips] = useState(new Set());
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [existingInterest, setExistingInterest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assignedTripIds, setAssignedTripIds] = useState(new Set());

  // Determine the recruitment year (next year's trips, or current year if trips exist)
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tripsRes, interestRes, myTripsRes] = await Promise.all([
          api.get('/trips'),
          api.get('/trip-interest/me'),
          api.get('/my-trips'),
        ]);

        // Trips the participant is already assigned to
        const assignedIds = new Set((myTripsRes.data || []).map(t => t.trip_id));
        setAssignedTripIds(assignedIds);

        // Show all open/upcoming trips (including assigned ones — they'll be greyed out)
        const available = (tripsRes.data || [])
          .filter(t => t.status !== 'completed' && new Date(t.start_date) > new Date())
          .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
        setTrips(available);

        // Get participant counts for each trip
        const counts = {};
        for (const trip of available) {
          counts[trip.trip_id] = trip.participant_count || 0;
        }
        setTripCounts(counts);

        // Check for existing interest — filter out trips already assigned
        const interests = interestRes.data || [];
        if (interests.length > 0) {
          const latest = interests[0];
          setExistingInterest(latest);
          const filteredPrefs = (latest.preferred_trip_ids || []).filter(id => !assignedIds.has(id));
          setSelectedTrips(new Set(filteredPrefs));
          setNotes(latest.notes || '');
        }
      } catch {
        toast.error('Failed to load trip data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async () => {
    if (selectedTrips.size === 0) {
      toast.error('Please select at least one preferred trip week');
      return;
    }
    setSubmitting(true);
    try {
      // Determine year from the first selected trip
      const firstTrip = trips.find(t => selectedTrips.has(t.trip_id));
      const year = firstTrip ? parseInt(firstTrip.start_date.substring(0, 4)) : currentYear + 1;

      await api.post('/trip-interest', {
        preferred_trip_ids: [...selectedTrips].filter(id => !assignedTripIds.has(id)),
        notes: notes.trim(),
        year,
      });
      toast.success('Trip interest submitted! The coordinator will review your request.');
      // Refresh
      const res = await api.get('/trip-interest/me');
      if (res.data?.length > 0) setExistingInterest(res.data[0]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleTrip = (tripId) => {
    if (assignedTripIds.has(tripId)) return; // Can't toggle assigned trips
    const next = new Set(selectedTrips);
    next.has(tripId) ? next.delete(tripId) : next.add(tripId);
    setSelectedTrips(next);
  };

  const isSubmitted = existingInterest?.status === 'pending';
  const isApproved = existingInterest?.status === 'approved';

  // Group trips by year
  const tripsByYear = {};
  trips.forEach(t => {
    const year = t.start_date?.substring(0, 4) || 'Unknown';
    if (!tripsByYear[year]) tripsByYear[year] = [];
    tripsByYear[year].push(t);
  });

  return (
    <PageLayout title="Trip Interest" headerSub="Let us know which mission weeks work for you" maxWidth="max-w-2xl">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : trips.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No upcoming trips available</p>
              <p className="text-slate-400 text-sm mt-1">Trip dates will be announced soon. Check back later!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Status banner */}
            {isApproved && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="py-4 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-green-800">You've been approved!</p>
                    <p className="text-sm text-green-600">Check your dashboard for trip assignment details.</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {isSubmitted && (
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="py-4 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-blue-800">Interest submitted!</p>
                    <p className="text-sm text-blue-600">The trip coordinator will review your request. You can update your preferences below.</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Trip selection */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Heart className="w-5 h-5 text-rojo" />
                  Which weeks work for you?
                </CardTitle>
                <CardDescription>
                  Select all the weeks you'd be available to join. The coordinator will assign you based on team needs and your preferences.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(tripsByYear).sort(([a], [b]) => a.localeCompare(b)).map(([year, yearTrips]) => (
                  <div key={year}>
                    <p className="text-sm font-semibold text-slate-500 mb-2">{year}</p>
                    <div className="space-y-2">
                      {yearTrips.map(trip => {
                        const isAssigned = assignedTripIds.has(trip.trip_id);
                        return (
                        <label
                          key={trip.trip_id}
                          className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                            isAssigned
                              ? 'border-slate-200 bg-slate-50 opacity-60 cursor-default'
                              : selectedTrips.has(trip.trip_id)
                                ? 'border-primary bg-primary/5 cursor-pointer'
                                : 'border-slate-200 hover:border-slate-300 cursor-pointer'
                          }`}
                        >
                          <Checkbox
                            checked={isAssigned || selectedTrips.has(trip.trip_id)}
                            onCheckedChange={() => toggleTrip(trip.trip_id)}
                            disabled={isAssigned}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className={`font-medium ${isAssigned ? 'text-slate-500' : 'text-slate-800'}`}>{trip.name}</p>
                              {isAssigned && <Badge className="bg-green-100 text-green-700 border-0 text-[10px]">Already Assigned</Badge>}
                            </div>
                            <p className="text-sm text-slate-500">
                              {trip.start_date} – {trip.end_date} · {trip.location || 'Antigua, Guatemala'}
                            </p>
                            {trip.description && (
                              <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{trip.description}</p>
                            )}
                            {!isAssigned && (
                              <div className="flex items-center gap-3 mt-2">
                                <span className="text-xs font-medium text-primary">
                                  {tripCounts[trip.trip_id] || 0} / {trip.max_participants || '–'} spots filled
                                </span>
                                {trip.max_participants && (
                                  <div className="flex-1 max-w-[120px] h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-primary rounded-full transition-all"
                                      style={{ width: `${Math.min(100, ((tripCounts[trip.trip_id] || 0) / trip.max_participants) * 100)}%` }}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          {trip.status === 'registration_open' && !isAssigned && (
                            <Badge className="bg-green-100 text-green-700 border-0 text-xs flex-shrink-0">Open</Badge>
                          )}
                        </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Anything else?</CardTitle>
                <CardDescription>
                  Schedule constraints, roommate preferences, or other notes for the coordinator.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="E.g., I can only do Week 1 due to work schedule. Would love to room with Dr. Smith again."
                  rows={4}
                  data-testid="interest-notes"
                />
              </CardContent>
            </Card>

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={submitting || selectedTrips.size === 0}
              className="w-full rounded-full bg-rojo hover:bg-rojo-dark h-12 text-base"
              data-testid="submit-interest-btn"
            >
              {submitting ? 'Submitting...' : isSubmitted ? 'Update My Preferences' : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Trip Interest
                </>
              )}
            </Button>
          </div>
        )}
    </PageLayout>
  );
};

export default TripInterestPage;
